/**
 * Communities API
 * API client for all communities endpoints
 * Base URL: /accounts/api/v1/communities
 */

import { BASE_ACCOUNTS_API } from '@/core/api-config';
import { TokenManager } from '@/core/auth/token-manager';
import { ApiErrorMiddleware } from '@/core/api-error-middleware';
import { useOperatorStore } from '@/core/stores/operator-store';
import type {
  GetCommunitiesResponse,
  GetCommunityResponse,
  InsertCommunityRequest,
  InsertCommunityResponse,
  UpdateCommunityRequest,
  UpdateCommunityResponse,
  UpsertCommunityAccountRelationRequest,
  UpsertCommunityAccountRelationResponse,
} from '../types/communities-types';
import type { CommunityAccountsResponse } from '../types/community-accounts-types';

export class CommunitiesApi {
  constructor(
    private readonly baseUrl: string = BASE_ACCOUNTS_API,
    private readonly tokenManager: TokenManager
  ) {}

  private async getAuthHeadersWithOperator(): Promise<Record<string, string>> {
    const selectedOperator = useOperatorStore.getState().selectedOperator;

    if (!selectedOperator) {
      console.error('CommunitiesApi: No operator selected, preventing API call');
      throw new Error('No operator selected. Please select an operator first.');
    }

    const headers = await this.tokenManager.getAuthHeaders();
    const operatorId = selectedOperator.operatorId.toString();

    const result: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers,
      'X-Operator-Id': operatorId,
    };

    // Only send X-APP-ID if appId exists and is not 0
    if (selectedOperator.appId && selectedOperator.appId !== 0) {
      result['X-APP-ID'] = selectedOperator.appId.toString();
    }

    return result;
  }

  /**
   * Get list of communities with optional filters
   */
  async getCommunities(
    includeExpired: boolean,
    actorId?: number
  ): Promise<GetCommunitiesResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const params = new URLSearchParams({
      includeExpired: includeExpired.toString(),
    });

    if (actorId) {
      params.append('actorId', actorId.toString());
    }

    const url = `${this.baseUrl}/communities?${params.toString()}`;

    try {
      const response = await ApiErrorMiddleware.fetchWithErrorHandling(
        url,
        { method: 'GET', headers },
        'Communities API',
        '/communities'
      );

      const data = await response.json();

      // Map backend field names to frontend types
      const mappedResponse: GetCommunitiesResponse = {
        ...data,
        communities: (data.communities || []).map((community: any) => ({
          ...community,
          assignedAccounts: community.assignedAccounts ?? 0,
        })),
      };

      return mappedResponse;
    } catch (error: unknown) {
      console.error('GET Communities Error:', error);
      // Handle 404 as empty result (no communities exist yet)
      if (error instanceof Error && error.message.includes('404')) {
        return {
          communities: [],
          success: true,
          message: null
        };
      }
      // Re-throw other errors
      throw error;
    }
  }

  /**
   * Get a single community by ID
   */
  async getCommunityById(id: number): Promise<GetCommunityResponse> {
    const headers = await this.getAuthHeadersWithOperator();

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/communities/${id}`,
      { method: 'GET', headers },
      'Communities API',
      `/communities/${id}`
    );

    return response.json();
  }

  /**
   * Create a new community
   */
  async createCommunity(data: InsertCommunityRequest): Promise<InsertCommunityResponse> {
    const headers = await this.getAuthHeadersWithOperator();

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/communities`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      },
      'Communities API',
      '/communities (POST)'
    );

    const result = await response.json();
    return result;
  }

  /**
   * Update an existing community
   */
  async updateCommunity(
    id: number,
    data: UpdateCommunityRequest
  ): Promise<UpdateCommunityResponse> {
    const headers = await this.getAuthHeadersWithOperator();

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/communities/${id}`,
      {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
      },
      'Communities API',
      `/communities/${id} (PUT)`
    );

    const result = await response.json();
    return result;
  }

  /**
   * Delete a community (soft or hard delete)
   * @param id - Community profile ID
   * @param hard - If true, permanent deletion; if false, soft delete (mark inactive)
   */
  async deleteCommunity(id: number, hard: boolean = false): Promise<void> {
    const headers = await this.getAuthHeadersWithOperator();
    const params = new URLSearchParams({
      hard: hard.toString(),
    });

    await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}/communities/${id}?${params.toString()}`,
      { method: 'DELETE', headers },
      'Communities API',
      `/communities/${id} (DELETE)`
    );
  }

  /**
   * Upsert (insert or update) an account-community relationship with date range
   * PUT /communities/account-relation
   */
  async upsertAccountRelation(
    communityProfileId: number,
    accountId: number,
    startDate: string,
    expirationDate: string | null = null
  ): Promise<UpsertCommunityAccountRelationResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/communities/account-relation`;

    // Get account ID from headers for updatedByName
    const accountIdFromHeader = headers['X-Account-Id'];
    const updatedByName = accountIdFromHeader && accountIdFromHeader !== 'unknown' && accountIdFromHeader !== 'undefined'
      ? accountIdFromHeader
      : '0'; // Default to '0' if no account ID available

    const body: UpsertCommunityAccountRelationRequest = {
      profileId: communityProfileId,
      accountId,
      startDate,
      expirationDate,
      updatedByName,
    };

    try {
      const response = await ApiErrorMiddleware.fetchWithErrorHandling(
        url,
        { method: 'PUT', headers, body: JSON.stringify(body) },
        'Communities API',
        '/communities/account-relation'
      );

      const data = await response.json();

      return data as UpsertCommunityAccountRelationResponse;
    } catch (error: unknown) {
      console.error('❌ PUT Upsert Account Relation Error:', error);
      throw error;
    }
  }

  /**
   * Get all operator accounts with their community association status
   * Returns ALL accounts for the current operator, with isAssignedToCommunity flag
   * indicating which accounts are associated with the specified community
   *
   * NOTE: If endpoint returns 404, returns empty array (backward compatibility)
   */
  async getCommunityAccounts(communityId: number): Promise<CommunityAccountsResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/communities/${communityId}/accounts`;

    try {
      const response = await ApiErrorMiddleware.fetchWithErrorHandling(
        url,
        { method: 'GET', headers },
        'Communities API',
        `/communities/${communityId}/accounts`
      );

      const data = await response.json();

      // Map API response to match our interface
      const mappedResponse: CommunityAccountsResponse = {
        profileId: data.profileId || communityId,
        accounts: (data.accounts || []).map((account: any) => ({
          accountId: account.accountId,
          firstName: account.firstName || '',
          lastName: account.lastName || '',
          status: account.statusId === 'Active' ? 'Active' as const : 'Inactive' as const,
          isAssignedToCommunity: account.isAssignedToCommunity || false,
          associatedDate: account.startDate ?? undefined,
          startDate: account.startDate ?? null,
          expirationDate: account.expirationDate ?? null,
        })),
        totalAccounts: data.totalAccounts || 0,
        linkedAccounts: data.linkedAccounts || 0,
      };

      return mappedResponse;
    } catch (error: unknown) {
      console.error('❌ GET Community Accounts Error:', error);
      // Handle 404 as empty result (no accounts associated yet)
      if (error instanceof Error && error.message.includes('404')) {
        return {
          profileId: communityId,
          accounts: [],
          totalAccounts: 0,
          linkedAccounts: 0,
        };
      }
      // Re-throw other errors
      throw error;
    }
  }
}
